import { Button, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { DsaUploadFolderDto } from "../../generated/dashboard-client";
import DSAApi from "../../api/DSAApi";
import "../../components/design/MoveToFolderModal.scss";
import { DownOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { DsaFolderThumb } from "./DsaFolderButton";
import FolderIcon from "../icons/FolderIcon";
import { DsaCreateFolderContent } from "./DsaCreateFolderContent";

export interface DsaMoveToFolderMenuContentProps {
  uploadIds: string[];
  folders: DsaUploadFolderDto[];
  activeFolder: DsaUploadFolderDto | null;
  onCloseMovedToFolder: (save: boolean, folder?: DsaUploadFolderDto) => void;
  onCloseMovedToUploads: (uploadIds: string[]) => void;
  onCloseMovedFolderToFolder: (childFolderId: number, parentFolderId: string) => void;
  folderToMove: DsaUploadFolderDto | null;
  folderThumbs: DsaFolderThumb[];
  folderNameExists: (folder: DsaUploadFolderDto | null, name: string) => boolean | undefined;
  onCreateFolderModalClose: (uploadIds: string[], folderToMove?: DsaUploadFolderDto | null, folder?: DsaUploadFolderDto) => void;
  onBack: () => void;
  isMobile: boolean;
}

export function DsaMoveToFolderMenuContent({
  uploadIds,
  folders,
  activeFolder,
  onCloseMovedToFolder,
  onCloseMovedToUploads,
  onCloseMovedFolderToFolder,
  onCreateFolderModalClose,
  folderToMove,
  folderThumbs,
  folderNameExists,
  onBack,
  isMobile
}: DsaMoveToFolderMenuContentProps) {
  const ref = useRef(null);
  const [folder, setFolder] = useState(undefined as DsaUploadFolderDto | undefined);
  const [allUploadsSelected, setAllUploadsSelected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shownFolders, setShownFolders] = useState([] as DsaUploadFolderDto[]);
  const [isTopLevelFolder, setIsTopLevelFolders] = useState(true);
  const [mode, setMode] = useState("selectFolder");

  useEffect(() => {
    setMode("selectFolder");
    setFolder(undefined);
    const newShownFolders = folders.filter(fol => {
      if (folderToMove && fol.id === folderToMove.id) return false;
      return !fol.parentId;
    });
    setShownFolders([...newShownFolders]);
    setIsTopLevelFolders(true);
  }, [folders, activeFolder, uploadIds, folderToMove]);

  function save() {
    if (!folder && !allUploadsSelected) return;
    const id = folder?.id!;

    setSaving(true);

    if (folderToMove) {
      if (allUploadsSelected) {
        DSAApi.removeFolderFromFolder(folderToMove.id!).then(() => {
          setSaving(false);
          onCloseMovedFolderToFolder(folderToMove.id!, "");
        });
      } else {
        if (folderToMove.id! === id) {
          message.error("Cannot move folder to itself!");
          setSaving(false);
        } else {
          DSAApi.putFolderInFolder(folderToMove.id!, id).then(() => {
            setSaving(false);
            onCloseMovedFolderToFolder(folderToMove.id!, id.toString());
          });
        }
      }
    } else {
      if (allUploadsSelected) {
        DSAApi.removeUploadsFromFolder(uploadIds)
          .then(() => {
            setSaving(false);
            onCloseMovedToUploads(uploadIds);
          })
          .catch(() => {
            setSaving(false);
          });
      } else {
        DSAApi.putUploadsInFolder(uploadIds, id)
          .then(() => {
            setSaving(false);
            onCloseMovedToFolder(true, folders.find(it => it.id === id));
          })
          .catch(() => {
            setSaving(false);
          });
      }
    }
  }

  function onFolderOptionClick(option: DsaUploadFolderDto) {
    setAllUploadsSelected(false);
    setFolder(option);
    const newShownFolders = folders.filter(fol => {
      if (folderToMove && fol.id === folderToMove.id) return false;
      return fol.parentId === option.id!.toString();
    });
    setShownFolders([...newShownFolders]);
    setIsTopLevelFolders(false);
  }

  function goToParent(option: DsaUploadFolderDto) {
    const parent = getParent(option);
    if (!parent) {
      setFolder(undefined);
      setIsTopLevelFolders(true);
      const newShownFolders = folders.filter(fol => {
        if (folderToMove && fol.id === folderToMove.id) return false;
        return !fol.parentId;
      });
      setShownFolders([...newShownFolders]);
    } else {
      onFolderOptionClick(parent);
    }
  }

  function hasChildren(option: DsaUploadFolderDto) {
    return folders.filter(fol => fol.parentId === option?.id?.toString()).length > 0;
  }

  function getParent(option: DsaUploadFolderDto) {
    let parent: DsaUploadFolderDto | undefined = undefined;
    folders.forEach(fol => {
      if (fol.id === parseInt(option.parentId!)) {
        parent = fol;
      }
    });
    return parent;
  }

  function onSelectAllUploads() {
    setAllUploadsSelected(true);
    setFolder(undefined);
  }

  function handleBack() {
    if (isMobile && mode === "selectFolder") {
      onBack();
    } else {
      setMode("selectFolder");
    }
  }

  const foldersOptions = (
    <div className={"folders-options"} ref={ref}>
      <ul>
        {activeFolder && (!folder || isTopLevelFolder) &&
          <li onClick={onSelectAllUploads} className={allUploadsSelected ? "active" : ""}>
            <div className="icon"><FolderIcon /></div>
            <div className="info"><b>All Uploads</b></div>
          </li>}
        {folder &&
          <li className={"folders-back"} onClick={() => goToParent(folder)}>
            <LeftOutlined />
            {folder.name}
          </li>}
        {shownFolders.map(it => (
          <li key={it.id} className={`${folder && folder.id === it.id ? "active" : ""}`} onClick={() => onFolderOptionClick(it)}>
            <div className="icon">
              <FolderIcon />
              <div
                className={"img-holder"}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "6px",
                  backgroundImage: `url(${folderThumbs.find(thumb => thumb.folderId === it.id!)?.thumb})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              />
            </div>
            <div className="info">
              <span><b>{it.name}</b></span>
              <span>{it.count} items</span>
            </div>
            {hasChildren(it) &&
              <div className="show-more">
                <RightOutlined />
              </div>}
          </li>
        ))}
      </ul>
      {shownFolders.length === 0 &&
        <div className="empty-message">
          Move here or create a new folder.
        </div>}
    </div>
  );

  return (
    <div className="move-to-folder-menu-content">
      <header>
        {(mode === "newFolder" || isMobile) &&
          <div className="handle-back" onClick={handleBack}>
            <LeftOutlined />
          </div>}
        <h2>Move to folder</h2>
        {isMobile && <div className="mobile-close" onClick={() => onCloseMovedToFolder(false)}><DownOutlined /></div>}
      </header>

      {mode === "selectFolder" && <>{foldersOptions}</>}

      {mode === "selectFolder" && <footer>
        <div>
          <Button
            className={"add-folder"}
            type={"link"}
            onClick={() => setMode("newFolder")}
          >
            + Create new folder
          </Button>
        </div>
        <div className={"action-btns"}>
          <Button size={"large"} onClick={() => onCloseMovedToFolder(false)}>
            Cancel
          </Button>
          <Button
            size={"large"}
            className={"primary"}
            onClick={save}
            disabled={!folder && !allUploadsSelected}
            loading={saving}
          >
            Move
          </Button>
        </div>
      </footer>}

      {mode === "newFolder" &&
        <div className="create-folder-modal">
          <DsaCreateFolderContent
            uploadIds={uploadIds}
            onClose={onCreateFolderModalClose}
            folderToMove={folderToMove}
            folderNameExists={folderNameExists}
            parentFolder={allUploadsSelected ? null : folder ?? null}
          />
        </div>}
    </div>
  );
}
